<script setup>
import { computed } from 'vue'

const props = defineProps({
  to: {
    type: String,
    required: true,
  },
  image: {
    type: String,
    default: '',
  },
  full: {
    type: String,
    default: '',
  },
  short: {
    type: String,
    default: '',
  },
  index: {
    type: Number,
    default: 0,
  },
  bgEmoji: {
    type: String,
    default: '',
  },
})

const buttonText = computed(() => {
  if (props.short) {
    return `${props.index + 1}. ${props.short}`
  }

  if (props.full) {
    return `${props.index + 1}. ${props.full}`
  }

  return ''
})
</script>

<template>
  <NuxtLink
    :to="to"
    class="tile-all"
    :title="buttonText"
  >
    <div class="tile-image">
      <img
        :alt="buttonText"
        :src="image"
      >
      <span
        v-if="bgEmoji"
        class="tile-bg-emoji"
        aria-hidden="true"
      >{{ bgEmoji }}</span>
    </div>

    <div class="tile-text prose dark:prose-invert">
      <div class="tile-title">
        {{ buttonText }}
      </div>
    </div>
  </NuxtLink>
</template>

<style lang="css" scoped>

/* Entire Tile */
.tile-all {
  @apply overflow-hidden rounded-xl shadow-md
         bg-gray-100 dark:bg-gray-800
         hover:bg-gray-200 hover:dark:bg-gray-700
         transition-all duration-200;

  aspect-ratio: 1 / 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 360px;
  margin-inline: auto;
}

/* IMAGE AREA = 360x240 proportion */
.tile-image {
  flex: 0 0 66.666%;
  overflow: hidden;
  position: relative;
}

/* Background emoji overlay */
.tile-bg-emoji {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(5rem, 20cqi, 9rem);
  opacity: 0.18;
  pointer-events: none;
  user-select: none;
  line-height: 1;
}

/* Perfect image scaling */
.tile-image img {
  @apply w-full h-full object-cover;
  object-position: center;
}

/* TEXT AREA = 360x120 proportion */
.tile-text {
  flex: 0 0 33.333%;
  @apply p-4 flex items-center justify-center text-center;
}

/* Title Styling */
.tile-title {
  @apply uppercase tracking-wide
    text-sm sm:text-base
         font-semibold leading-snug;
  font-size: clamp(0.875rem, 2.2cqi, 1rem);
  display: block;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

/* Zoom Effect on Hover */
.tile-image img {
  @apply w-full h-full object-cover transition-transform duration-300;
}

.tile-all:hover .tile-image img {
  transform: scale(1.05);
}
</style>